import React, { useState, useEffect } from 'react';
import { Play, Pause, RotateCcw, CheckCircle, Clock, AlertCircle, Zap, Film } from 'lucide-react';
import LiveWorkflowEngine from './LiveWorkflowEngine';

/**
 * Workflow Tab Component
 * 
 * Visualizes the complete A2A communication workflow
 * Shows step-by-step message exchanges between agents
 * NOW WITH LIVE AI MODE! 🚀
 */
const WorkflowTab = () => {
  const [mode, setMode] = useState('animated'); // 'animated' or 'live'
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [selectedWorkflow, setSelectedWorkflow] = useState('basic');
  
  // Backend URL for live mode
  const backendUrl = import.meta.env.VITE_BACKEND_URL || 'https://a2ateachingaids-server.appsokay.net';

  const workflows = {
    basic: {
      name: 'Basic Learning Request',
      description: 'Simple content generation workflow',
      steps: [
        {
          id: 1,
          agent: 'client',
          action: 'User clicks "Learn About Capabilities"',
          message: {
            action: 'execute',
            task: 'generate_learning_content',
            parameters: {
              topic: 'capability_discovery',
              difficulty: 'beginner'
            }
          },
          description: 'Client Agent sends request to backend',
          duration: 500
        },
        {
          id: 2,
          agent: 'remote',
          action: 'Receives and validates request',
          message: {
            status: 'received',
            validation: 'passed',
            timestamp: new Date().toISOString()
          },
          description: 'Remote Agent verifies message signature and parameters',
          duration: 300
        },
        {
          id: 3,
          agent: 'remote',
          action: 'Calls DeepSeek AI for content generation',
          message: {
            prompt: 'Generate educational content about capability discovery...',
            model: 'deepseek-chat'
          },
          description: 'AI Agent creates educational content',
          duration: 2000
        },
        {
          id: 4,
          agent: 'database',
          action: 'Saves generated module to database',
          message: {
            table: 'ta_learning_modules',
            operation: 'INSERT',
            module_id: 'mod-12345'
          },
          description: 'Database Agent persists the content',
          duration: 400
        },
        {
          id: 5,
          agent: 'remote',
          action: 'Returns content to client',
          message: {
            success: true,
            module_id: 'mod-12345',
            content: {
              title: 'Understanding Capability Discovery',
              sections: ['...'],
              quiz: ['...']
            }
          },
          description: 'Client receives and displays content',
          duration: 300
        },
        {
          id: 6,
          agent: 'client',
          action: 'Renders content with animations',
          message: {
            status: 'rendered',
            ui_state: 'learning_content_displayed'
          },
          description: 'User sees the educational content',
          duration: 500
        }
      ]
    },
    simulation: {
      name: 'Interactive Simulation',
      description: 'Running a capability discovery simulation',
      steps: [
        {
          id: 1,
          agent: 'client',
          action: 'User starts simulation',
          message: {
            action: 'execute',
            task: 'simulate_a2a_interaction',
            parameters: {
              simulation_type: 'capability_discovery',
              complexity: 'basic'
            }
          },
          description: 'Client initiates simulation',
          duration: 500
        },
        {
          id: 2,
          agent: 'remote',
          action: 'Creates simulation environment',
          message: {
            status: 'simulation_initialized',
            agents: ['HR_Agent', 'Recruitment_Agent']
          },
          description: 'Remote Agent sets up virtual agents',
          duration: 600
        },
        {
          id: 3,
          agent: 'remote',
          action: 'Simulates Agent A discovering capabilities',
          message: {
            from: 'HR_Agent',
            to: 'Recruitment_Agent',
            action: 'discover_capabilities'
          },
          description: 'Virtual agents exchange discovery messages',
          duration: 800
        },
        {
          id: 4,
          agent: 'remote',
          action: 'Simulates Agent B responding with capabilities',
          message: {
            from: 'Recruitment_Agent',
            to: 'HR_Agent',
            capabilities: ['screen_candidates', 'schedule_interviews']
          },
          description: 'Capability list is returned',
          duration: 800
        },
        {
          id: 5,
          agent: 'remote',
          action: 'Evaluates simulation performance',
          message: {
            errors: 0,
            messages_exchanged: 2,
            success: true,
            rating: 4
          },
          description: 'AI assesses the interaction',
          duration: 1000
        },
        {
          id: 6,
          agent: 'database',
          action: 'Saves simulation results',
          message: {
            table: 'ta_simulations',
            operation: 'INSERT',
            results: { success: true, rating: 4 }
          },
          description: 'Progress is recorded',
          duration: 400
        },
        {
          id: 7,
          agent: 'client',
          action: 'Displays results and feedback',
          message: {
            status: 'complete',
            feedback: 'Great job! All capabilities were discovered correctly.'
          },
          description: 'User sees performance feedback',
          duration: 500
        }
      ]
    },
    assessment: {
      name: 'Quiz Assessment',
      description: 'Taking a quiz and receiving feedback',
      steps: [
        {
          id: 1,
          agent: 'client',
          action: 'User submits quiz answers',
          message: {
            action: 'execute',
            task: 'assess_understanding',
            parameters: {
              topic: 'capability_discovery',
              answers: [1, 3, 2, 4]
            }
          },
          description: 'Quiz responses sent for grading',
          duration: 500
        },
        {
          id: 2,
          agent: 'remote',
          action: 'AI evaluates answers',
          message: {
            correct_answers: [1, 3, 2, 4],
            user_answers: [1, 3, 2, 4],
            score: 100
          },
          description: 'DeepSeek AI grades the quiz',
          duration: 1500
        },
        {
          id: 3,
          agent: 'remote',
          action: 'Generates personalized feedback',
          message: {
            feedback: 'Excellent! You have mastered capability discovery.',
            recommendations: ['Try advanced simulations', 'Explore task execution']
          },
          description: 'AI creates learning recommendations',
          duration: 1200
        },
        {
          id: 4,
          agent: 'database',
          action: 'Updates learning progress',
          message: {
            table: 'ta_learning_progress',
            operation: 'UPDATE',
            progress_percent: 100,
            status: 'completed'
          },
          description: 'Progress is saved',
          duration: 400
        },
        {
          id: 5,
          agent: 'client',
          action: 'Shows results and next steps',
          message: {
            score: 100,
            badge: 'Capability Discovery Expert',
            next_topic: 'task_execution'
          },
          description: 'User receives achievement',
          duration: 500
        }
      ]
    }
  };

  const currentWorkflow = workflows[selectedWorkflow];
  const totalSteps = currentWorkflow.steps.length;

  useEffect(() => {
    let timer;
    if (isPlaying && currentStep < totalSteps) {
      const step = currentWorkflow.steps[currentStep];
      timer = setTimeout(() => {
        setCompletedSteps(new Set([...completedSteps, currentStep]));
        setCurrentStep(currentStep + 1);
      }, step.duration);
    } else if (currentStep >= totalSteps) {
      setIsPlaying(false);
    }
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, totalSteps, currentWorkflow.steps, completedSteps]);

  const handlePlayPause = () => {
    if (currentStep >= totalSteps) {
      // Reset if completed
      handleReset();
    }
    setIsPlaying(!isPlaying);
  };

  const handleReset = () => {
    setIsPlaying(false);
    setCurrentStep(0);
    setCompletedSteps(new Set());
  };

  const getAgentColor = (agent) => {
    const colors = {
      client: 'from-blue-500 to-indigo-600',
      remote: 'from-purple-500 to-pink-600',
      database: 'from-green-500 to-emerald-600',
      ai: 'from-orange-500 to-red-600'
    };
    return colors[agent] || 'from-gray-500 to-gray-600';
  };

  const getAgentIcon = (agent) => {
    const icons = {
      client: '👤',
      remote: '⚡',
      database: '💾',
      ai: '🤖'
    };
    return icons[agent] || '📦';
  };

  const getStepStatus = (index) => {
    if (completedSteps.has(index)) return 'completed';
    if (index === currentStep) return 'active';
    return 'pending';
  };

  // Map animated workflow keys to live workflow keys
  const workflowMapping = {
    basic: 'learning_request',
    simulation: 'interactive_simulation',
    assessment: 'quiz_assessment'
  };

  return (
    <div className="space-y-6">
      {/* Mode Selector */}
      <div className="card p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">A2A Workflow Visualization</h2>
            <p className="text-sm text-gray-600 mt-1">
              {mode === 'animated' ? 'Watch pre-scripted animations' : 'See real AI agents in action'}
            </p>
          </div>
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setMode('animated')}
              className={`px-6 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                mode === 'animated'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Film className="w-4 h-4" />
              Animated
            </button>
            <button
              onClick={() => setMode('live')}
              className={`px-6 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                mode === 'live'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Zap className="w-4 h-4" />
              Live AI
              <div className={`w-2 h-2 rounded-full ${mode === 'live' ? 'bg-red-500 animate-pulse' : 'bg-gray-400'}`}></div>
            </button>
          </div>
        </div>
      </div>

      {/* Conditional Rendering: Live or Animated */}
      {mode === 'live' ? (
        <div>
          {/* Workflow Selection for Live Mode */}
          <div className="card p-4 mb-6">
            <h3 className="font-bold text-gray-900 mb-3">Select Live AI Workflow</h3>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(workflows).map(([key, workflow]) => (
                <button
                  key={key}
                  onClick={() => setSelectedWorkflow(key)}
                  className={`p-4 text-left rounded-lg border-2 transition-all ${
                    selectedWorkflow === key
                      ? 'border-indigo-600 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  <div className="font-bold text-gray-900">{workflow.name}</div>
                  <div className="text-sm text-gray-600 mt-1">{workflow.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Live Workflow Engine */}
          <LiveWorkflowEngine
            workflow={workflowMapping[selectedWorkflow]}
            backendUrl={backendUrl}
            onComplete={() => {
              console.log('Live workflow completed!');
            }}
          />
        </div>
      ) : (
        <>
          {/* Original Animated Workflow */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">A2A Communication Workflow</h2>
                <p className="text-gray-600 mt-1">Watch how agents collaborate step-by-step</p>
              </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleReset}
              className="btn-secondary flex items-center space-x-2"
              disabled={currentStep === 0 && !isPlaying}
            >
              <RotateCcw className="w-4 h-4" />
              <span>Reset</span>
            </button>
            <button
              onClick={handlePlayPause}
              className="btn-primary flex items-center space-x-2 px-6"
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              <span>{isPlaying ? 'Pause' : currentStep >= totalSteps ? 'Replay' : 'Play'}</span>
            </button>
          </div>
        </div>

        {/* Workflow Tabs */}
        <div className="flex space-x-2 border-b border-gray-200">
          {Object.entries(workflows).map(([key, workflow]) => (
            <button
              key={key}
              onClick={() => {
                setSelectedWorkflow(key);
                handleReset();
              }}
              className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                selectedWorkflow === key
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {workflow.name}
            </button>
          ))}
        </div>

        <p className="text-gray-600 mt-4">{currentWorkflow.description}</p>

        {/* Progress Bar */}
        <div className="mt-6">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progress</span>
            <span>{currentStep} / {totalSteps} steps</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-indigo-600 to-purple-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Workflow Steps */}
      <div className="space-y-4">
        {currentWorkflow.steps.map((step, index) => {
          const status = getStepStatus(index);
          const isActive = status === 'active';
          const isCompleted = status === 'completed';

          return (
            <div
              key={step.id}
              className={`card p-6 transition-all duration-300 ${
                isActive ? 'ring-2 ring-indigo-600 shadow-lg scale-[1.02]' : ''
              } ${isCompleted ? 'opacity-75' : ''}`}
            >
              <div className="flex items-start space-x-4">
                {/* Step Number & Status */}
                <div className="flex-shrink-0">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white bg-gradient-to-br ${
                    isCompleted ? 'from-green-500 to-emerald-600' : 
                    isActive ? getAgentColor(step.agent) : 
                    'from-gray-300 to-gray-400'
                  }`}>
                    {isCompleted ? <CheckCircle className="w-6 h-6" /> : step.id}
                  </div>
                </div>

                {/* Step Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="text-2xl">{getAgentIcon(step.agent)}</span>
                    <div>
                      <div className="font-bold text-gray-900">{step.action}</div>
                      <div className="text-sm text-gray-500">{step.description}</div>
                    </div>
                  </div>

                  {/* Message Preview */}
                  {(isActive || isCompleted) && (
                    <div className="mt-3 bg-gray-900 rounded-lg p-4 overflow-x-auto animate-fadeIn">
                      <pre className="text-xs text-gray-100 font-mono">
                        {JSON.stringify(step.message, null, 2)}
                      </pre>
                    </div>
                  )}

                  {/* Duration Badge */}
                  <div className="mt-3 flex items-center space-x-2">
                    {isActive && (
                      <span className="badge bg-indigo-100 text-indigo-800 flex items-center space-x-1 animate-pulse-slow">
                        <Clock className="w-3 h-3" />
                        <span>Processing... ({step.duration}ms)</span>
                      </span>
                    )}
                    {isCompleted && (
                      <span className="badge bg-green-100 text-green-800 flex items-center space-x-1">
                        <CheckCircle className="w-3 h-3" />
                        <span>Completed in {step.duration}ms</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Completion Message */}
      {currentStep >= totalSteps && (
        <div className="card p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 animate-fadeIn">
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-gray-900 text-lg">Workflow Complete! 🎉</h3>
              <p className="text-gray-700 mt-1">
                You've seen how {totalSteps} steps work together seamlessly in the A2A system.
                Each agent plays a crucial role in delivering a smooth learning experience.
              </p>
              <button
                onClick={handleReset}
                className="mt-4 btn-primary flex items-center space-x-2"
              >
                <RotateCcw className="w-4 h-4" />
                <span>Watch Again</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Learning Tips */}
      <div className="card p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-bold text-gray-900">💡 Understanding the Flow</h3>
            <ul className="mt-2 space-y-1 text-sm text-gray-700">
              <li>• Each step represents a real message exchange between agents</li>
              <li>• Messages are JSON objects following the A2A protocol</li>
              <li>• Agents process requests asynchronously for better performance</li>
              <li>• Error handling ensures reliability at every step</li>
              <li>• Try different workflows to see various patterns!</li>
            </ul>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default WorkflowTab;

